import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { CheckCircle, Sparkle, Target, Warning } from 'phosphor-react';

import { DiagramaCamadas } from './diagramas';
import {
  Callout,
  ExemploTrabalhado,
  Forte,
  MiniKpiRow,
  Paragrafo,
  SecaoEnsino,
  SubTitulo,
  Termo,
  TocLayout,
} from './ui';

const SECOES = [
  { id: 'camadas', label: 'As 3 camadas' },
  { id: 'sinais', label: 'Sinais que decidem' },
  { id: 'validador', label: 'O validador' },
  { id: 'placar', label: 'Quão bem funciona' },
];

const VEREDITOS: { selo: string; cor: string; bg: string; desc: string }[] = [
  {
    selo: 'AUTO',
    cor: '#059669',
    bg: 'rgba(16,185,129,0.1)',
    desc: 'todos os eixos altos → concilia sozinho',
  },
  {
    selo: 'SUGERE',
    cor: '#B45309',
    bg: 'rgba(245,158,11,0.12)',
    desc: 'eixo médio → pré-preenche, você confirma',
  },
  {
    selo: 'CONFLITO',
    cor: '#DC2626',
    bg: 'rgba(239,68,68,0.1)',
    desc: 'razão e coerência divergem → mostra os dois lados',
  },
  {
    selo: 'PENDENTE',
    cor: '#475569',
    bg: 'rgba(15,23,42,0.06)',
    desc: 'sinal insuficiente → vai pra IA / humano',
  },
];

export function AbaComoDecide() {
  return (
    <TocLayout secoes={SECOES}>
      <SecaoEnsino id="camadas" numero={1} titulo="As 3 camadas: L1 → L2 → L3">
        <Paragrafo>
          O ContaCil resolve a contrapartida na camada{' '}
          <Forte>mais barata e confiável primeiro</Forte>, e só desce para a próxima se a anterior
          não resolveu. A filosofia central: <Forte>“a IA é o resíduo, não a regra”</Forte>.
        </Paragrafo>
        <Box
          as="ul"
          pl={5}
          maxW="68ch"
          color="#334155"
          fontSize="15px"
          lineHeight="1.7"
          sx={{ '& li': { mb: 2 } }}
        >
          <li>
            <Forte>L1 — Regras determinísticas:</Forte> reconhece padrões fixos do histórico
            (tarifa, IOF, impostos, rendimentos, transferências óbvias). Grátis e instantâneo.
            Traduz{' '}
            <Text as="span" fontStyle="italic">
              texto → categoria → classificação-âncora → código da empresa
            </Text>
            .
          </li>
          <li>
            <Forte>L2 — Memória aprendida:</Forte> aprende com o{' '}
            <Termo def="O livro-razão: os lançamentos que os contadores já fizeram no Domínio.">
              razão real
            </Termo>{' '}
            e com <Forte>as suas correções</Forte>. Aprende por contraparte (“todo pagamento ao
            fornecedor X vai para a conta Y”) e nunca mais pergunta a mesma coisa.
          </li>
          <li>
            <Forte>L3 — IA, só no resíduo:</Forte> roda apenas no que L1 e L2 não cobriram —
            contraparte nova ou ambígua. Abaixo de um limiar de confiança, vira{' '}
            <Forte>sugestão para você confirmar</Forte>, não auto-aplica.
          </li>
        </Box>
        <DiagramaCamadas />
        <ExemploTrabalhado titulo="Thiago, o sócio — descendo as camadas">
          <Text mb={2}>
            Um “Pix para Thiago Rossi”: <Forte>L1</Forte> detecta que é sócio (nome bate no
            cadastro) → grupo <code>116</code>. A escolha entre <Forte>adiantamento de lucro</Forte>{' '}
            (cód. 31) e <Forte>empréstimo</Forte> (cód. 35) sai de uma palavra-chave no histórico
            (“adiantamento” vs “mútuo”).
          </Text>
          <Text>
            Sem palavra que decida, o sistema <Forte>mostra 31 e 35 ranqueados</Forte> — não chuta.
            Você escolhe 31; <Forte>L2 memoriza</Forte> e, daí em diante, todo Thiago daquela
            empresa já sugere 31 no topo.
          </Text>
        </ExemploTrabalhado>
      </SecaoEnsino>

      <SecaoEnsino id="sinais" numero={2} titulo="Os sinais que determinam a contrapartida">
        <Paragrafo>
          São as “pistas” que o sistema lê no extrato para descobrir a outra conta:
        </Paragrafo>
        <Box
          as="ul"
          pl={5}
          maxW="68ch"
          color="#334155"
          fontSize="15px"
          lineHeight="1.7"
          sx={{ '& li': { mb: 2 } }}
        >
          <li>
            <Forte>Sócio</Forte> — a contraparte é PF que consta como sócio → grupo <code>116</code>
            (lucro ou empréstimo, conforme a natureza).
          </li>
          <li>
            <Forte>Transferência entre contas próprias (“espelho”)</Forte> — mesmo valor sai de uma
            conta e entra em outra da empresa, em poucos dias.{' '}
            <Forte>Não é despesa nem fornecedor</Forte>: a contrapartida é a conta-banco da outra
            ponta. É o sinal mais forte.
          </li>
          <li>
            <Forte>Aplicação / resgate</Forte> — dinheiro indo investir (saída) ou voltando
            (entrada) → conta de aplicação (<code>11103</code>), não conta de resultado.
          </li>
          <li>
            <Forte>Cliente pagando</Forte> — uma entrada cujo CNPJ/CPF bate no cadastro de clientes
            → sugere Clientes / a receita certa.
          </li>
          <li>
            <Forte>Fornecedor</Forte> — o CNPJ identifica <Forte>quem</Forte> é, mas{' '}
            <Forte>não diz qual conta de despesa</Forte> — isso vem da memória (L2).
          </li>
          <li>
            <Forte>Impostos e tarifas pelo texto</Forte> — “SIMPLES NACIONAL”, “TARIFA”, “ECAD”,
            “CONTABILIDADE” → conta correspondente.
          </li>
        </Box>
        <Callout tone="aviso" titulo="O CNPJ é um sinal fraco no extrato">
          Ele aparece em poucos lançamentos e, em entradas, costuma ser do banco/adquirente. Por
          isso o sistema casa primeiro por <Forte>nome normalizado</Forte>{' '}
          (sócio/cliente/fornecedor) e usa o CNPJ como reforço.
        </Callout>
      </SecaoEnsino>

      <SecaoEnsino id="validador" numero={3} titulo="O validador: por que uma sugestão é confiável">
        <Callout tone="info" titulo="A premissa">
          <Forte>“O razão é professor, não evangelho.”</Forte> O histórico do Domínio contém erros
          (rendimento lançado em despesa, contas provisórias usadas como atalho). Aprender cego =
          herdar o erro. Por isso toda sugestão passa por um juiz antes de auto-aplicar.
        </Callout>
        <Paragrafo>
          A confiança é o <Forte>mínimo entre três eixos</Forte> — se qualquer um for baixo, o
          sistema não aplica sozinho:
        </Paragrafo>
        <Box
          as="ul"
          pl={5}
          maxW="68ch"
          color="#334155"
          fontSize="15px"
          lineHeight="1.7"
          sx={{ '& li': { mb: 2 } }}
        >
          <li>
            <Forte>Confiabilidade da fonte</Forte> — ignora conta provisória, contrapartida vazia,
            padrão com baixa pureza (mesma contraparte indo a contas diferentes), pouquíssimas
            ocorrências, estornos, contas inativas.
          </li>
          <li>
            <Forte>Coerência contábil</Forte> — débito/crédito coerente com a natureza; rendimento
            positivo não vai em conta negativa; a conta tem que ser analítica, da empresa e ativa; e
            a contrapartida <Forte>nunca</Forte> pode ser a própria perna-banco.
          </li>
          <li>
            <Forte>Validação cruzada</Forte> — bate com NF? o CNPJ é fornecedor/cliente/sócio? L1 e
            L2 concordam? há recorrência mensal? as outras empresas do grupo fazem igual?
          </li>
        </Box>
        <SubTitulo>Os quatro veredictos</SubTitulo>
        <Flex direction="column" gap={2} maxW="68ch" my={3}>
          {VEREDITOS.map(v => (
            <Flex key={v.selo} align="center" gap={3} bg={v.bg} borderRadius="lg" px={3} py={2}>
              <Text fontSize="11px" fontWeight="800" color={v.cor} minW="76px">
                {v.selo}
              </Text>
              <Text fontSize="13px" color="#334155">
                {v.desc}
              </Text>
            </Flex>
          ))}
        </Flex>
        <ExemploTrabalhado titulo="quando o validador discorda do razão">
          O contador lançou um <Forte>rendimento de aplicação</Forte> (entrada, dc=C) numa conta de{' '}
          <Forte>despesa</Forte>. O validador detecta a incoerência (crédito em conta de despesa),
          marca <Forte>CONFLITO</Forte> e propõe a conta de <Forte>receita</Forte> — virando também
          um detector de erro de lançamento.
        </ExemploTrabalhado>
        <Callout tone="sucesso" titulo="Sua palavra vale mais">
          A sua correção tem prioridade sobre o razão (você é a autoridade na sua empresa) — mas
          fica registrada, é reversível e também passa pela checagem de coerência.
        </Callout>
      </SecaoEnsino>

      <SecaoEnsino id="placar" numero={4} titulo="Quão bem isto funciona (placar honesto)">
        <Paragrafo>Medido em lançamentos reais (sem duplicatas), combinando L1 + L2:</Paragrafo>
        <MiniKpiRow
          kpis={[
            {
              label: 'Auto',
              valor: '~43%',
              icon: <CheckCircle size={18} weight="duotone" />,
              accent: '#10B981',
              sub: '99% de acerto',
            },
            {
              label: 'Sugere',
              valor: '~28%',
              icon: <Sparkle size={18} weight="duotone" />,
              accent: '#F59E0B',
              sub: 'você confirma',
            },
            {
              label: 'Resíduo',
              valor: '~30%',
              icon: <Target size={18} weight="duotone" />,
              accent: '#6366F1',
              sub: 'IA / NF / humano',
            },
            {
              label: 'Não chuta',
              valor: '0',
              icon: <Warning size={18} weight="duotone" />,
              accent: '#EF4444',
              sub: 'abaixo do limiar não aplica',
            },
          ]}
        />
        <Paragrafo>
          A IA caiu de “100% dos lançamentos” para ~30%, e o que sobra é genuinamente novo — sem
          gastar tokens no caso comum. A cobertura <Forte>cresce</Forte> conforme o contador posta
          mais meses no Domínio: o sistema aprende dos meses passados e aplica no mês corrente.
        </Paragrafo>
        <Callout tone="aviso" titulo="Leitura honesta">
          Os números vêm de empresas-piloto (EDITORA ROSSI, JET MUSIC, CRS MUSIC e outras). São
          indicativos e melhoram com mais dados. Onde há sinal claro (financeiro, sócio, aplicação),
          a precisão é altíssima; onde a contraparte é nova, ainda depende de você.
        </Callout>
      </SecaoEnsino>
    </TocLayout>
  );
}
